export enum EDomain {
  CATEGORY = 'categories',
  BRAND = 'brands',
  VENDOR = 'vendors',
  PRODUCT = 'products',
  PRICING_TEMPLATES = 'pricingTemplates',
}

export enum EAutoOnboardingStepStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export interface ImportCollectionStatus {
  status: EAutoOnboardingStepStatus
  createdAt?: string
  updatedAt?: string
  error?: unknown
  fileName?: string
  signedUrl?: string
  fileEmpty?: boolean
  urlExpiresAt?: string
}

export type ImportSyncStatusResponse = Partial<Record<EDomain, ImportCollectionStatus>>

export enum CollectionType {
  CategoryMigration = 'CategoryMigration',
  VendorMigration = 'VendorMigration',
  BrandMigration = 'BrandMigration',
  ProductMigration = 'ProductMigration',
  PricingTemplateMigration = 'PricingTemplateMigration',
}

export enum MigrationCollections {
  Company = 'Company',
  Shops = 'Shops',
  Employees = 'Employees',
  Brands = 'Brands',
  Vendors = 'Vendors',
  Categories = 'Categories',
  Products = 'Products',
  PricingTemplates = 'Pricing Templates',
}

export enum MigrationStatus {
  COMPLETED = 'Completed',
  QUEUED = 'Queued',
  NOT_SYNCED = 'Failed',
  ERROR = 'Error',
  IN_PROGRESS = 'InProgress',
  //...
}
export interface CollectionMigrationAttributes {
  itemsCreated: null | number
  dirty: boolean
  created: number
  type: CollectionType
  userId: string
  errorMsg: null | string
  companyId: string
  deleted: boolean
  modified: number
  shopId: string
  id: string
  asset: null | MigrationAsset
  updated: boolean
  status: MigrationStatus
}

interface MigrationAsset {
  id: string
  created: number
  modified: number
  deleted: boolean
  updated: boolean
  companyId: string
  name: string
  key: string
  type: string
  publicURL: unknown
  active: boolean
  priority: number
  secured: boolean
  thumbURL: unknown
  mediumURL: unknown
  largeURL: unknown
  largeX2URL: unknown
  origURL: unknown
  platformFileUrl: unknown
  assetType: string
}
export interface GetMigrationsResponse {
  data: {
    attributes: {
      limit: number
      skip: number
      total: number
      values: CollectionMigrationAttributes[]
    }
  }
}

export interface MigrationResponse {
  data: {
    attributes: CollectionMigrationAttributes
  }
}
export interface MigrationStatusResponse {
  data: {
    attributes: CollectionMigrationAttributes
  }
}

export interface CompanyShopsAndEmployeesMigrationResponse {
  data: CompanyShopsAndEmployeesMigrationAttributes
}

interface CompanyShopsAndEmployeesMigrationAttributes {
  platformCompanyId: string
  syncedEmployeeIds: string[]
  syncedShopIds: string[]
}

export interface CompanySnapshotListResponse {
  skip: number
  limit: number
  total: number
  values: CompanySnapshotItem[]
}

export interface CompanySnapshotItem {
  id: string
  requestDate: number
  status: CompanySnapshotStatus
  reportType: string
  companyId: string
  format?: string
  shopId?: string
  asset?: {
    id: string
    created: number
    modified: number
    deleted: boolean
    key: string
    name: string
  }
}

export enum CompanySnapshotStatus {
  COMPLETED = 'Completed',
  QUEUED = 'Queued',
  IN_PROGRESS = 'InProgress',
  ERROR = 'Error',
  EXPIRED = 'Expired',
}

// New Product Migration Types
export enum ProductMigrationStep {
  PREVALIDATE = 'prevalidate',
  DEDUPE_VALIDATE = 'dedupe-validate',
  BULK_CREATE = 'bulk-create',
  UPDATE_PLATFORM_IDS = 'update-shop-platform-productids',
  VALIDATE_MIGRATION = 'validate-migration',
  SYNC_OPEN_SEARCH = 'sync-open-search',
}

export enum ProductMigrationStepStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  FAILED = 'FAILED',
  DONE = 'DONE', // Changed from COMPLETED to DONE
  ERROR = 'FAILED', // Alias for FAILED for backward compatibility
}

// API Response structure from actual API response
export interface ProductMigrationStepResult {
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'RUNNING' | 'FAILED' | 'DONE'
  error: string | null
}

export interface ProductMigrationStatusResponse {
  companyId: string
  lastChecked: string // Date
  lastUpdatedStatus: string // Date
  dedupe: ProductMigrationStepResult
  bulkCreate: ProductMigrationStepResult
  updatePlatformProductIds: ProductMigrationStepResult
}

export interface ProductMigrationStepInfo {
  step: ProductMigrationStep
  status: ProductMigrationStepStatus
  processId?: string
  message?: string
  startedAt?: number
  completedAt?: number
}

export interface ProductMigrationState {
  steps: ProductMigrationStepInfo[]
  currentStep: number
  isRunning: boolean
}

// Validation Response Types
export interface ProductMigrationValidationError {
  productName: string
  greenlineProductId: string
  type: string
  usaShops: string[]
  expectedOverrides: number
  actualOverrides: number
  details: string[]
}

export interface ProductMigrationValidationResponse {
  companyId: string
  validationTimestamp: string
  processingTime: string
  totalUsaProducts: number
  totalCanProducts: number
  uniqueProductNames: number
  validationResults: {
    passed: number
    failed: number
  }
  errors: ProductMigrationValidationError[]
  summary: string
}

export enum EProductExportDedupeKey {
  NAME = 'name',
  SKU = 'SKU',
  MASTER_ID = 'masterId',
  BRAND = 'brandName',
  CATEGORY = 'categoryName',
}



// WEBPACK FOOTER //
// ./src/types/migration.ts